import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Check } from "lucide-react";
import { tr } from "date-fns/locale";

const ConfirmSyncDialog = ({ open, onClose, onConfirm, title, description }) => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const handleKey = (e) => {
            if (!open) return;
            if (e.key === "Escape") handleClose();
            if (e.key === "Enter") handleAction();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [open]);

    const handleAction = async (isSync) => {
        setLoading(true);
        const res = await onConfirm(isSync);

        if (res) {
            setTimeout(() => {
                setSuccess(true);
                setLoading(false);
            }, 400);

            setTimeout(() => handleClose(), 1400);
        } else {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setLoading(false);
        setSuccess(false);
        onClose(false);
    };

    return (
        <>
            {/* ===== CLEAN MODERN STYLE ===== */}
            <style>{`
                [data-radix-dialog-overlay] {
                    background: rgba(0,0,0,0.08);
                    backdrop-filter: blur(6px);
                }

                [data-radix-dialog-content] {
                    border-radius: 16px;
                    overflow: hidden;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(8px) scale(0.97); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>

            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="p-0 max-w-md w-[92vw]">

                    <div className="bg-white dark:bg-gray-900 p-6">

                        {/* ===== SUCCESS STATE ===== */}
                        {success ? (
                            <div className="text-center">
                                <div className="flex justify-center mb-4">
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <Check className="text-green-600 w-6 h-6" />
                                    </div>
                                </div>

                                <h2 className="text-lg font-semibold">
                                    {title} synced
                                </h2>

                                <p className="text-sm text-gray-500 mt-1">
                                    Shipment data has been linked successfully
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* ===== HEADER (AI STYLE) ===== */}
                                <div className="flex items-start gap-3">

                                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                        {loading ? (
                                            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                                        ) : (
                                            <Sparkles className="w-5 h-5 text-blue-600" />
                                        )}
                                    </div>

                                    <div className="text-left">
                                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                                            Sync {title} with shipment?
                                        </h2>

                                        <p className="text-sm text-gray-500 mt-1">
                                            Matching shipment details found. You can sync automatically to keep everything aligned.
                                        </p>

                                        <div className="mt-2 text-xs text-blue-600">
                                            Smart suggestion: Auto sync to reduce manual work
                                        </div>
                                    </div>
                                </div>

                                {/* ===== ACTIONS ===== */}
                                <div className="mt-6 flex justify-end gap-3">

                                    {/* Skip */}
                                    <Button
                                        variant="ghost"
                                        onClick={() => handleAction(false)}
                                        disabled={loading}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        Skip
                                    </Button>

                                    {/* Primary */}
                                    <Button
                                        onClick={() => handleAction(true)}
                                        disabled={loading}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-5"
                                    >
                                        {loading ? (
                                            <span className="flex items-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Syncing...
                                            </span>
                                        ) : (
                                            "Auto Sync"
                                        )}
                                    </Button>

                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ConfirmSyncDialog;
